import { c as _c } from "react/compiler-runtime";
import './Map.css';
import { viewBox, bugPaths, cyborgPaths, illuminatePaths, superEarthCircle, factionIcons } from '@/features/galaxy/mapPaths.mjs';
import { highlightCard, clearCardHighlight } from '@/features/galaxy/sectorLink.mjs';

/**
 * Renders the galaxy map SVG. The root `<svg>` uses
 * `preserveAspectRatio="xMaxYMid meet"` — content scales to fit the
 * box while aligning to the right edge and vertically centered.
 * This choice matters in two places:
 *
 *   - **Desktop (lg+)**: the grid column's width is sized from
 *     viewport height via `minmax(0, calc((100dvh - 80px) * 806.93 /
 *     868.81))`, so the SVG box matches the content's aspect ratio
 *     exactly and `xMax` is a no-op — content fills the cell.
 *
 *   - **Mobile/tablet when pinned**: `.home-map--sticky` /
 *     `.archives-map-col--sticky` caps the SVG's `max-height: 55dvh`
 *     and `max-width: calc(55dvh * 806.93 / 868.81)` so the box
 *     matches the intrinsic aspect ratio. Again `xMax` alignment is
 *     a no-op because there's no leftover horizontal space inside
 *     the SVG. `margin-inline: auto` on the SVG then centers the
 *     whole shrunk box inside the full-bleed sticky panel.
 *
 * The `#map` wrapper div provides a flex-column sizing context so
 * the SVG's `flex-1` + `min-h-0` resolves correctly when the parent
 * has an explicit max-height. Any other SVG rendered elsewhere in
 * the map column (e.g. icon SVGs in the archive's detail card)
 * isn't affected because the CSS selectors are scoped to
 * `#map > svg`.
 */

const factions = [{
  id: 'bugs',
  index: 0,
  paths: bugPaths
}, {
  id: 'cyborgs',
  index: 1,
  paths: cyborgPaths
}, {
  id: 'illuminate',
  index: 2,
  paths: illuminatePaths
}];

/**
 * @param {Map<string, number> | undefined} delays - Per-key pulse delays in seconds, keyed by region.
 * @param {string} key - The region key to look up a pulse delay for.
 * @returns {React.CSSProperties | undefined}
 */
function pulseStyle(delays, key) {
  const delay = delays?.get(key);
  return delay != null ? (/** @type {React.CSSProperties} */{
    '--pulse-delay': `${delay}s`
  }) : undefined;
}
export default function Map(t0) {
  const $ = _c(17);
  const {
    map,
    pulseDelays
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%"><feDropShadow dx="5" dy="5" stdDeviation="2" floodColor="rgba(255,255,0, 1.0)" /></filter>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <filter id="glow"><feGaussianBlur className="blur" result="coloredBlur" stdDeviation="4" /><feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="coloredBlur" /><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <defs>{t1}{t2}<filter id="glow-red" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur in="SourceGraphic" result="blur" stdDeviation="6" /><feFlood floodColor="rgba(255,0,0,0.6)" result="color" /><feComposite in="color" in2="blur" operator="in" result="redGlow" /><feMerge><feMergeNode in="redGlow" /><feMergeNode in="SourceGraphic" /></feMerge></filter></defs>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== map || $[4] !== pulseDelays) {
    t4 = factions.map(t5 => {
      const {
        id,
        index,
        paths
      } = t5;
      return <g key={id} id={id} onMouseEnter={() => highlightCard(index)} onMouseLeave={clearCardHighlight}>{paths.map(path => <path key={path.id} id={path.id} data-name={String(path.sector)} data-faction={id} className={path.sector === 11 ? "sector " + map[index][11].status : "sector " + map[index][path.sector].status + " " + map[index][path.sector].event} style={pulseStyle(pulseDelays, `${index}-${path.sector}`)} d={path.d} />)}<image href={factionIcons[index].href} className="pointer-events-none" x={factionIcons[index].x} y={factionIcons[index].y} width={factionIcons[index].width} height={factionIcons[index].height} /></g>;
    });
    $[3] = map;
    $[4] = pulseDelays;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = "sector " + map[3][0].status + " " + map[3][0].event;
  let t6;
  if ($[6] !== pulseDelays) {
    t6 = pulseStyle(pulseDelays, "3-0");
    $[6] = pulseDelays;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t5 || $[9] !== t6) {
    t7 = <circle id={superEarthCircle.id} data-name="0" className={t5} style={t6} cx={superEarthCircle.cx} cy={superEarthCircle.cy} r={superEarthCircle.r} />;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <image className="pointer-events-none" href={factionIcons[3].href} x={factionIcons[3].x} y={factionIcons[3].y} width={factionIcons[3].width} height={factionIcons[3].height} />;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t7) {
    t9 = <g id="superearth" onMouseEnter={_temp} onMouseLeave={clearCardHighlight}>{t7}{t8}</g>;
    $[12] = t7;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t4 || $[15] !== t9) {
    t10 = <div id="map" className="flex min-h-0 w-full flex-1 flex-col"><svg id="Layer_2" data-name="Layer 2" xmlns="http://www.w3.org/2000/svg" viewBox={viewBox} preserveAspectRatio="xMaxYMid meet" className="min-h-0 w-full min-w-0 flex-1">{t3}{t4}{t9}</svg></div>;
    $[14] = t4;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  return t10;
}
function _temp() {
  return highlightCard(3);
}